import { prisma } from "@/lib/db";
import {
  DECAY_LAMBDA,
  FOCUS_SET_SIZE,
  MASTERY_THRESHOLD,
  NEW_ITEM_P_CORRECT,
} from "./constants";

/**
 * Calculate p_correct using exponential decay over last 10 attempts
 *
 * weight(i) = e^(-λ * hours_since_attempt_i)
 * p_correct = Σ(weight_i * correct_i) / Σ(weight_i)
 */
export async function calculatePCorrect(
  userId: string,
  problemId: string,
  now: Date
): Promise<number> {
  // Get last 10 review logs for this problem
  const logs = await prisma.reviewLog.findMany({
    where: {
      userId,
      problemId,
    },
    orderBy: { timestamp: "desc" },
    take: 10,
  });

  // If no attempts, return default for new items
  if (logs.length === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  // Calculate weighted sum
  let weightedSum = 0;
  let totalWeight = 0;

  for (const log of logs) {
    const hoursSince = (now.getTime() - log.timestamp.getTime()) / (1000 * 60 * 60);
    const weight = Math.exp(-DECAY_LAMBDA * hoursSince);

    weightedSum += weight * (log.wasCorrect ? 1 : 0);
    totalWeight += weight;
  }

  // Avoid division by zero (shouldn't happen if logs.length > 0)
  if (totalWeight === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  return weightedSum / totalWeight;
}

/**
 * Synchronous p_correct calculation when we already have the logs
 */
export function calculatePCorrectFromLogs(
  logs: Array<{ timestamp: Date; wasCorrect: boolean }>,
  now: Date
): number {
  if (logs.length === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const log of logs) {
    const hoursSince = (now.getTime() - log.timestamp.getTime()) / (1000 * 60 * 60);
    const weight = Math.exp(-DECAY_LAMBDA * hoursSince);

    weightedSum += weight * (log.wasCorrect ? 1 : 0);
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    return NEW_ITEM_P_CORRECT;
  }

  return weightedSum / totalWeight;
}

/**
 * Check if Focus set has reached mastery (90% correct)
 */
export async function checkFocusMastery(userId: string): Promise<{
  mastered: boolean;
  correctCount: number;
  totalCount: number;
}> {
  const focusProblems = await prisma.userProblemState.findMany({
    where: {
      userId,
      set: "focus",
    },
  });

  const totalCount = focusProblems.length;
  const correctCount = focusProblems.filter(p => p.lastResult === "correct").length;
  const mastered = totalCount > 0 && correctCount / totalCount >= MASTERY_THRESHOLD;

  return { mastered, correctCount, totalCount };
}

/**
 * Move all Focus problems to Old and grab next 20 from New
 */
export async function promoteFocusSet(userId: string): Promise<number> {
  // Move all Focus → Old
  await prisma.userProblemState.updateMany({
    where: {
      userId,
      set: "focus",
    },
    data: {
      set: "old",
    },
  });

  // Get problems that don't have a state (truly new)
  const newProblems = await prisma.problem.findMany({
    where: {
      userProblemStates: {
        none: {
          userId,
        },
      },
    },
    orderBy: [
      { groupId: "asc" },
      { sequenceInGroup: "asc" },
    ],
    take: FOCUS_SET_SIZE,
  });

  // Create states for new problems as "focus"
  if (newProblems.length > 0) {
    await prisma.userProblemState.createMany({
      data: newProblems.map(p => ({
        userId,
        problemId: p.id,
        set: "focus",
      })),
    });
  }

  return newProblems.length;
}

/**
 * Initialize Focus set for a new user (grab first 20 problems)
 */
export async function initializeFocusSet(userId: string): Promise<number> {
  // Check if user already has focus problems
  const existingFocus = await prisma.userProblemState.count({
    where: {
      userId,
      set: "focus",
    },
  });

  if (existingFocus > 0) {
    return existingFocus;
  }

  // Get first 20 problems
  const problems = await prisma.problem.findMany({
    where: {
      userProblemStates: {
        none: {
          userId,
        },
      },
    },
    orderBy: [
      { groupId: "asc" },
      { sequenceInGroup: "asc" },
    ],
    take: FOCUS_SET_SIZE,
  });

  if (problems.length > 0) {
    await prisma.userProblemState.createMany({
      data: problems.map(p => ({
        userId,
        problemId: p.id,
        set: "focus",
      })),
    });
  }

  return problems.length;
}

/**
 * Get counts for each set
 */
export async function getSetCounts(userId: string): Promise<{
  newCount: number;
  focusCount: number;
  oldCount: number;
  focusCorrectCount: number;
}> {
  const totalProblems = await prisma.problem.count();

  const [focusStates, oldCount] = await Promise.all([
    prisma.userProblemState.findMany({
      where: { userId, set: "focus" },
    }),
    prisma.userProblemState.count({
      where: { userId, set: "old" },
    }),
  ]);

  const focusCount = focusStates.length;
  const focusCorrectCount = focusStates.filter(s => s.lastResult === "correct").length;
  const newCount = totalProblems - focusCount - oldCount;

  return { newCount, focusCount, oldCount, focusCorrectCount };
}
